import type {App,DirectiveBinding} from 'vue'
type Kind='query'|'login'|'save'
type Action={kind:Kind;run:(event:KeyboardEvent)=>unknown;pending:boolean;last:number}
const accepted=new Set(['query','login','save'])
const ime=new WeakMap<Document,{composing:boolean;ended:number}>()
export function isSafeEnter(event:KeyboardEvent){
  const owner=(event.target as Node|null)?.ownerDocument,state=owner?ime.get(owner):undefined
  return !event.isComposing && event.keyCode!==229 && !event.repeat && !event.ctrlKey && !event.altKey && !event.metaKey && !event.shiftKey && !state?.composing && (!state?.ended || Date.now()-state.ended>=180)
}
export function installEnterActions(app:App,doc:Document=document){
  const actions=new Map<HTMLElement,Action>()
  const composition={composing:false,ended:0};ime.set(doc,composition)
  const visible=(el:HTMLElement)=>el.isConnected && el.getClientRects().length>0 && !el.closest('[hidden],[aria-hidden="true"]') && doc.defaultView?.getComputedStyle(el).visibility!=='hidden'
  const disabled=(el:HTMLElement)=>el.hasAttribute('disabled') || el.getAttribute('aria-disabled')==='true' || el.getAttribute('aria-busy')==='true' || el.classList.contains('ant-btn-loading')
  const scope=(el:HTMLElement)=>el.closest<HTMLElement>('[data-enter-scope]')
  const stop=(event:KeyboardEvent,prevent=false)=>{if(prevent)event.preventDefault();event.stopImmediatePropagation()}
  const isEnter=(event:KeyboardEvent)=>event.key==='Enter' || event.keyCode===13
  const blockedInput=(el:HTMLElement)=>el.tagName!=='INPUT' || el.isContentEditable || !!el.closest('textarea,[contenteditable="true"],[role="textbox"][aria-multiline="true"],.ant-select,.ant-picker,.ant-cascader,.ant-mentions') || ['button','submit','checkbox','radio','file','range','color'].includes((el as HTMLInputElement).type)
  const inTopDialog=(el:HTMLElement)=>{
    const dialogs=Array.from(doc.querySelectorAll<HTMLElement>('[role="dialog"],[role="alertdialog"],.ant-modal-wrap,.ant-drawer-content-wrapper')).filter(visible)
    if(!dialogs.length)return true
    const last=dialogs[dialogs.length-1]
    return last.contains(el) || !!el.closest('[role="dialog"]') && el.closest('[role="dialog"]')!.contains(last)
  }
  const onStart=()=>{composition.composing=true}
  const onEnd=()=>{composition.composing=false;composition.ended=Date.now()}
  const onKeyDown=(event:KeyboardEvent)=>{
    if(!isEnter(event))return
    const target=event.target as HTMLElement|null,area=target && scope(target)
    if(!target || !area)return
    // 多行与选择控件保持原交互；keyup仍拦截旧的冒泡回车查询，避免选中选项时误查。
    if(blockedInput(target))return
    if(composition.composing || event.isComposing || event.keyCode===229 || Date.now()-composition.ended<180){stop(event);return}
    if(event.ctrlKey || event.altKey || event.shiftKey || event.metaKey){stop(event);return}
    if(!inTopDialog(target)){stop(event,true);return}
    const kind=area.getAttribute('data-enter-scope') as Kind
    const activeDialog=target.closest<HTMLElement>('[role="dialog"],.ant-modal-content,.ant-drawer-content')
    let boundary:HTMLElement=activeDialog && !activeDialog.contains(area) ? activeDialog : area
    let matches=Array.from(actions).filter(([el,a])=>a.kind===kind && boundary.contains(el) && visible(el))
    if(!matches.length){const dialog=area.closest<HTMLElement>('[role="dialog"],.ant-modal-content,.ant-drawer-content');if(dialog){boundary=dialog;matches=Array.from(actions).filter(([el,a])=>a.kind===kind && boundary.contains(el) && visible(el))}}
    // 入口不唯一时不猜测；同一作用域只允许显式标记且可用的动作。
    stop(event,true)
    if(matches.length!==1 || event.repeat)return
    const [button,action]=matches[0]
    if(action.pending || disabled(button) || target.hasAttribute('disabled') || target.hasAttribute('readonly') || Date.now()-action.last<650)return
    action.pending=true;action.last=Date.now()
    try{Promise.resolve(action.run(event)).catch(()=>{/* 原保存/查询流程负责显示失败，不自动重放。 */}).finally(()=>{action.pending=false})}catch{action.pending=false}
  }
  const onKeyUp=(event:KeyboardEvent)=>{
    if(!isEnter(event))return
    const target=event.target as HTMLElement|null
    if(target && scope(target) && target.tagName==='INPUT')stop(event)
  }
  const bind=(el:HTMLElement,binding:DirectiveBinding)=>{
    if(!accepted.has(binding.arg || '') || typeof binding.value!=='function'){actions.delete(el);return}
    const old=actions.get(el)
    if(old){old.kind=binding.arg as Kind;old.run=binding.value;return}
    actions.set(el,{kind:binding.arg as Kind,run:binding.value,pending:false,last:0})
  }
  app.directive('enter-action',{mounted:bind,updated:bind,unmounted:(el:HTMLElement)=>actions.delete(el)})
  doc.addEventListener('compositionstart',onStart,true);doc.addEventListener('compositionend',onEnd,true)
  doc.addEventListener('keydown',onKeyDown,true);doc.addEventListener('keyup',onKeyUp,true)
  return ()=>{actions.clear();doc.removeEventListener('compositionstart',onStart,true);doc.removeEventListener('compositionend',onEnd,true);doc.removeEventListener('keydown',onKeyDown,true);doc.removeEventListener('keyup',onKeyUp,true)}
}
